/**
 * Réinitialisation du mot de passe — PRD §6.1.3
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CountryPhoneInput from '../components/forms/CountryPhoneInput.jsx';
import PasswordInput from '../components/forms/PasswordInput.jsx';
import { defaultCountry } from '../data/countries.js';
import { getCountryFromPhoneNumber } from '../utils/phone-country-alignment.js';
import * as authService from '../services/auth.service.js';
import { useErrorToast, useSuccessToast } from '../hooks/useErrorToast.js';
import './ForgotPassword.css';

const STEPS = {
  PHONE: 1,
  CODE: 2,
  PASSWORD: 3,
  DONE: 4,
};

function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.PHONE);
  const [telephone, setTelephone] = useState(defaultCountry.phoneCode);
  const [countryCode, setCountryCode] = useState(defaultCountry.code);
  const [verificationCode, setVerificationCode] = useState('');
  const [passwordResetToken, setPasswordResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [codeMessage, setCodeMessage] = useState('');
  const [mockOtpCode, setMockOtpCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useErrorToast(error);
  useSuccessToast(step === STEPS.DONE ? 'Mot de passe réinitialisé avec succès' : '');

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handlePhoneChange = (fullPhoneNumber) => {
    setTelephone(fullPhoneNumber);
    const detected = getCountryFromPhoneNumber(fullPhoneNumber);
    if (detected?.code) {
      setCountryCode(detected.code);
    }
    if (error) setError('');
  };

  const buildSendCodeMessage = (channel, data) => {
    if (data?.mock_code) {
      return 'Mode test : utilisez le code affiché ci-dessous.';
    }
    if (channel === 'voice') {
      return 'Vous allez recevoir un appel vocal qui vous dictera votre code.';
    }
    return data?.message || 'Si ce numéro est enregistré, un code vous a été envoyé par SMS.';
  };

  const handleSendCode = async () => {
    if (!telephone || telephone.length < 8) {
      setError('Veuillez saisir un numéro de téléphone valide.');
      return;
    }

    setSendingCode(true);
    setError('');
    setCodeMessage('');
    setMockOtpCode('');
    try {
      const res = await authService.sendPasswordResetCode(telephone, { countryCode });
      if (res.success) {
        const channel = res.data?.channel || 'sms';
        setCodeMessage(buildSendCodeMessage(channel, res.data));
        if (res.data?.mock_code) {
          setMockOtpCode(res.data.mock_code);
        }
        setResendCooldown(60);
        setStep(STEPS.CODE);
      } else {
        setError(res.error || 'Impossible d\'envoyer le code');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible d\'envoyer le code');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Veuillez saisir le code reçu.');
      return;
    }

    setVerifyingCode(true);
    setError('');
    try {
      const res = await authService.verifyPasswordResetCode(telephone, verificationCode, { countryCode });
      if (res.success && res.data?.password_reset_token) {
        setPasswordResetToken(res.data.password_reset_token);
        setCodeMessage('Code vérifié ✓');
        setStep(STEPS.PASSWORD);
      } else {
        setError(res.error || 'Code invalide');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await authService.resetPassword(
        telephone,
        passwordResetToken,
        newPassword,
        confirmPassword,
      );
      if (res.success) {
        setStep(STEPS.DONE);
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      } else {
        setError(res.error || 'Erreur lors de la réinitialisation');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la réinitialisation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <h1>Grand-Électeur</h1>
        <h2>Mot de passe oublié</h2>

        <p className="forgot-password-hint">
          {step === STEPS.PHONE && 'Saisissez votre numéro de téléphone pour recevoir un code de vérification.'}
          {step === STEPS.CODE && 'Entrez le code à 6 chiffres reçu par SMS ou appel vocal.'}
          {step === STEPS.PASSWORD && 'Choisissez un nouveau mot de passe sécurisé.'}
          {step === STEPS.DONE && 'Votre mot de passe a été mis à jour. Redirection vers la connexion…'}
        </p>

        {step === STEPS.PHONE && (
          <section aria-labelledby="forgot-step-phone">
            <h3 id="forgot-step-phone" className="sr-only">Saisir votre numéro de téléphone</h3>
            <div className="form-group">
              <label htmlFor="forgot-telephone">
                Numéro de téléphone <span className="required" aria-label="requis">*</span>
              </label>
              <CountryPhoneInput
                id="forgot-telephone"
                value={telephone}
                onChange={handlePhoneChange}
                disabled={sendingCode}
              />
            </div>
            <div className="form-submit-row">
              <button
                type="button"
                className="btn-primary"
                onClick={handleSendCode}
                disabled={sendingCode}
                aria-busy={sendingCode}
              >
                {sendingCode ? 'Envoi…' : 'Envoyer le code'}
              </button>
            </div>
          </section>
        )}

        {step === STEPS.CODE && (
          <section aria-labelledby="forgot-step-code">
            <h3 id="forgot-step-code" className="sr-only">Saisir le code de vérification</h3>
            {codeMessage && (
              <p className="forgot-code-message" role="status">{codeMessage}</p>
            )}
            {mockOtpCode && (
              <p className="forgot-mock-code" role="status">
                Code test : <strong>{mockOtpCode}</strong>
              </p>
            )}
            <div className="form-group">
              <label htmlFor="forgot-code">
                Code de vérification <span className="required" aria-label="requis">*</span>
              </label>
              <input
                id="forgot-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={verificationCode}
                onChange={(e) => {
                  setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                  if (error) setError('');
                }}
                className="form-control forgot-code-input"
                disabled={verifyingCode}
                aria-required="true"
              />
            </div>
            <div className="form-submit-row forgot-actions-row">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep(STEPS.PHONE)}
                disabled={verifyingCode}
              >
                Retour
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleVerifyCode}
                disabled={verifyingCode || verificationCode.length < 4}
                aria-busy={verifyingCode}
              >
                {verifyingCode ? 'Vérification…' : 'Vérifier'}
              </button>
            </div>
            <p className="forgot-resend-row">
              <button
                type="button"
                className="btn-link"
                onClick={handleSendCode}
                disabled={sendingCode || resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Renvoyer le code (${resendCooldown}s)` : 'Renvoyer le code'}
              </button>
            </p>
          </section>
        )}

        {step === STEPS.PASSWORD && (
          <form onSubmit={handleResetPassword} aria-label="Nouveau mot de passe">
            <div className="form-group">
              <PasswordInput
                id="forgot-new-password"
                name="newPassword"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (error) setError('');
                }}
                label={
                  <>
                    Nouveau mot de passe <span className="required" aria-label="requis">*</span>
                  </>
                }
                required
                disabled={submitting}
                autoComplete="new-password"
                hint="Au moins 8 caractères, une majuscule, une minuscule et un chiffre."
                hintId="forgot-password-hint"
              />
            </div>
            <div className="form-group">
              <PasswordInput
                id="forgot-confirm-password"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (error) setError('');
                }}
                label={
                  <>
                    Confirmer le mot de passe <span className="required" aria-label="requis">*</span>
                  </>
                }
                required
                disabled={submitting}
                autoComplete="new-password"
              />
            </div>
            <div className="form-submit-row">
              <button
                type="submit"
                className="btn-primary"
                disabled={submitting}
                aria-busy={submitting}
              >
                {submitting ? 'Enregistrement…' : 'Réinitialiser le mot de passe'}
              </button>
            </div>
          </form>
        )}

        {step === STEPS.DONE && (
          <div className="forgot-success" role="status">
            <p>Mot de passe mis à jour avec succès.</p>
            <Link to="/login" className="btn-primary">Se connecter</Link>
          </div>
        )}

        <div className="form-footer">
          <p>
            <Link to="/login">Retour à la connexion</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
